/**
 * ActionBar Block — Primary + secondary CTAs
 *
 * Can be used both as a standalone block within the blocks array
 * and as the inline global action bar at the bottom of DynamicViewV2.
 *
 * Variants:
 *   primary   → solid cyan bg, dark text
 *   secondary → cyan border, transparent bg
 *   ghost     → text only, subtle hover
 *
 * Action types: deeplink, chat, calendar, clipboard, share
 */
import React from 'react';
import type { ActionBarData } from '../types';

// =============================================================================
// INLINE ICON HELPERS (no external deps)
// =============================================================================

const IconExternalLink: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
    <polyline points="15 3 21 3 21 9" />
    <line x1="10" y1="14" x2="21" y2="3" />
  </svg>
);

const IconCopy: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const IconShare: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);

const IconCalendar: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const IconChat: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

/** Map action type → default icon */
const ACTION_ICONS: Record<string, React.FC<{ className?: string }>> = {
  deeplink: IconExternalLink,
  chat: IconChat,
  calendar: IconCalendar,
  clipboard: IconCopy,
  share: IconShare,
};

// =============================================================================
// COMPONENT
// =============================================================================

interface ActionBarProps {
  data: ActionBarData;
  onAction?: (prompt: string) => void;
}

export const ActionBar: React.FC<ActionBarProps> = ({ data, onAction }) => {
  const { actions } = data;
  if (!actions || actions.length === 0) return null;

  const handleClick = (action: ActionBarData['actions'][number]) => {
    if (action.disabled) return;

    switch (action.type) {
      case 'deeplink':
        if (action.url) {
          window.open(action.url, '_blank', 'noopener,noreferrer');
        }
        break;
      case 'chat':
        if (action.prompt && onAction) {
          onAction(action.prompt);
        }
        break;
      case 'calendar':
        // Calendar events flow through onAction with a special prefix
        if (onAction) {
          onAction(action.prompt || `Add calendar event: ${action.label}`);
        }
        break;
      case 'clipboard':
        if (action.url) {
          navigator.clipboard.writeText(action.url).catch(() => {});
        } else if (action.prompt) {
          navigator.clipboard.writeText(action.prompt).catch(() => {});
        }
        break;
      case 'share':
        if (navigator.share && action.url) {
          navigator.share({ url: action.url, title: action.label }).catch(() => {});
        } else if (action.url) {
          navigator.clipboard.writeText(action.url).catch(() => {});
        }
        break;
    }
  };

  return (
    <div className="flex flex-wrap gap-3">
      {actions.map((action) => {
        const Icon = action.icon
          ? ACTION_ICONS[action.icon] || ACTION_ICONS[action.type]
          : ACTION_ICONS[action.type];

        const variantClasses =
          action.variant === 'primary'
            ? 'bg-cyan-400 text-black hover:bg-cyan-300 shadow-[0_0_20px_rgba(34,211,238,0.4)]'
            : action.variant === 'secondary'
              ? 'bg-cyan-400/15 text-cyan-400 hover:bg-cyan-400/25 border border-cyan-400/40'
              : 'text-cyan-400/60 hover:text-cyan-300';

        const disabledClasses = action.disabled
          ? 'opacity-40 cursor-not-allowed pointer-events-none'
          : 'active:scale-95';

        return (
          <button
            key={action.id}
            onClick={() => handleClick(action)}
            disabled={action.disabled}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium text-sm transition-all duration-200 ${variantClasses} ${disabledClasses}`}
          >
            {Icon && <Icon className="w-4 h-4" />}
            {action.label}
          </button>
        );
      })}
    </div>
  );
};

export default ActionBar;
